"use client";

import { SearchInput } from "@money/frontend/components/ui/search-input";
import { Button } from "@money/frontend/components/ui/button.tsx";
import { useCallback, useEffect, useState, useRef } from "react";
import { useModals } from "@money/frontend/store/store.ts";
import { ProviderComponent } from "@money/frontend/components/leaderboard/provider.component.tsx";
import { LoginComponent } from "@money/frontend/components/auth/login.component.tsx";
import { useUser } from "@money/frontend/components/hooks/use.user.tsx";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { SearchResults } from "@money/frontend/components/leaderboard/search-results.component.tsx";

interface SearchResult {
  id: string;
  name: string;
  logoUrl: string | null;
  slug: string;
}

export function LeaderboardHeader() {
  const modals = useModals();
  const user = useUser();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [_, setIsSearching] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const addStartup = useCallback(() => {
    modals.show({
      label: "Choose Provider",
      component: (close) => <ProviderComponent close={close} />,
    });
  }, []);

  const openLogin = useCallback(() => {
    modals.show({
      label: "",
      component: (close) => <LoginComponent close={close} />,
    });
  }, []);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }

    // Set loading state
    setIsSearching(true);

    // Debounce search
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
        if (!backendUrl) {
          throw new Error("NEXT_PUBLIC_BACKEND_URL is not set");
        }

        const response = await fetch(
          `${backendUrl}/billing/search?q=${encodeURIComponent(searchQuery)}&limit=10`
        );

        if (!response.ok) {
          throw new Error("Search failed");
        }

        const results = await response.json();
        setSearchResults(results);
        setIsSearchOpen(true);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Logo and Buttons */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex items-center gap-2">
          <span>💰</span>
          <div className="flex flex-col sm:flex-row items-start sm:items-baseline gap-0.5 sm:gap-1.5">
            <Link href="/" className="text-gray-900 font-semibold text-base sm:text-lg">valid.cash</Link>
            <Link href="https://postiz.com" className="text-gray-500 text-xs sm:text-sm font-normal">by postiz.com</Link>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <Button onClick={addStartup} className="text-sm sm:text-base flex-1 sm:flex-initial">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <path
                d="M8 3.33333V12.6667M3.33333 8H12.6667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="hidden sm:inline">Add startup</span>
            <span className="sm:hidden">Add</span>
          </Button>
          {user ? (
            <Button variant="secondary" onClick={() => router.push("/dashboard/company")} className="flex-1 sm:flex-initial">
              Dashboard
            </Button>
          ) : (
            <Button onClick={openLogin} className="flex-1 sm:flex-initial">
              Login
            </Button>
          )}
        </div>
      </div>

      {/* Main Title */}
      <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
        Startups Monies List 🤑
      </h1>

      {/* Search */}
      <div className="flex items-center justify-center relative">
        <div className="relative flex-1 max-w-md w-full">
          <SearchInput
            placeholder="Search startups..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <SearchResults
            results={searchResults}
            onClose={handleCloseSearch}
            isOpen={isSearchOpen && searchResults.length > 0}
          />
        </div>
      </div>
    </div>
  );
}
